import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import ImportFileMapper from 'Components/Organisms/ImportFileCSV/ImportFileMapper';
import ImportFileError from 'Components/Organisms/ImportFileCSV/ImportFileError';
import { mapContacts } from 'Services/invoices';

export default function ContactsImportMapperDrawer({ onClose, importId, columns, suggestion }) {
    const [processing, setProcessing] = useState(false);
    const [errorType, setErrorType] = useState(null);
    const [errorInfo, setErrorInfo] = useState(null);
    const { t } = useTranslation('contacts');
    const queryClient = useQueryClient();

    const handleSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
        onClose();
    };

    const fields = useMemo(
        () => [
            {
                id: 'id',
                label: t('id'),
                required: false,
                deduplicationColumns: true,
                unique: true,
            },
            {
                id: 'name',
                label: t('name'),
                required: true,
                deduplicationColumns: true,
                unique: false,
            },
            {
                id: 'type',
                label: t('type'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'subtype',
                label: t('subtype'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'note',
                label: t('internal-note'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'tax_id',
                label: t('tax-id'),
                required: false,
                deduplicationColumns: true,
                unique: false,
            },
            {
                id: 'tax_id_type',
                label: t('tax-id-type'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'email',
                label: t('email'),
                required: false,
                deduplicationColumns: true,
                unique: false,
            },
            {
                id: 'phone_number',
                label: t('phone-number'),
                required: false,
                deduplicationColumns: true,
                unique: false,
            },
            {
                id: 'website',
                label: t('website'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'address',
                label: t('address'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'postal_code',
                label: t('postal-code'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'city',
                label: t('city'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'state',
                label: t('state'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'country',
                label: t('country'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'language',
                label: t('language'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'currency',
                label: t('currency'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'due_days',
                label: t('due-days'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'payment_day',
                label: t('payment-day'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'taxonomies_tags',
                label: t('tags'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'categorization_keywords',
                label: t('categorization-keywords'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'taxes_incomes',
                label: t('taxes-incomes'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
            {
                id: 'taxes_expenses',
                label: t('taxes-expenses'),
                required: false,
                deduplicationColumns: false,
                unique: false,
            },
        ],
        [t],
    );

    return (
        <div className="h-full pb-4 flex flex-col">
            {errorType ? (
                <ImportFileError onClose={onClose} type={errorType} info={errorInfo} />
            ) : (
                <ImportFileMapper
                    buttonText={t('add-contacts')}
                    onClose={onClose}
                    columns={columns}
                    importId={importId}
                    suggestion={suggestion}
                    processing={processing}
                    onChangeProcessing={setProcessing}
                    mapService={mapContacts}
                    fields={fields}
                    onSubmit={({ error, errorInfo: info }) => {
                        if (!error) {
                            handleSuccess();
                        } else {
                            setErrorType(error);
                            setErrorInfo(info);
                        }
                    }}
                />
            )}
        </div>
    );
}

ContactsImportMapperDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    importId: PropTypes.string.isRequired,
    columns: PropTypes.array.isRequired,
    suggestion: PropTypes.shape({
        columns_mapping: PropTypes.object,
        deduplication_columns: PropTypes.arrayOf(PropTypes.string),
    }),
};
